import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { euro } from '../../lib/format';
import { useToasts } from '../../lib/toast';
import { useDevice } from '../../store';
import { useLang } from '../../lib/lang';
import { useLiveOrders } from '../../lib/useLiveOrders';
import { socket } from '../../lib/socket';
import { ReopenModal } from './ReopenModal';
import type { ReportSummary, PartnerRow } from '@taj/types';

const METHODS: { k: string; fr: string; en: string; i: string; c: string }[] = [
  { k: 'CARD', fr: 'Carte Bleue & Carte ticket', en: 'Card & meal card', i: '💳', c: '#8a5cc0' },
  { k: 'CARTE_TICKET', fr: 'Carte Bleue & Carte ticket', en: 'Card & meal card', i: '💳', c: '#8a5cc0' },
  { k: 'ONLINE', fr: 'En ligne', en: 'Online', i: '🏦', c: '#1f9150' },
  { k: 'TICKET_RESTO', fr: 'Ticket papier', en: 'Paper voucher', i: '🎟️', c: '#e08a1e' },
  { k: 'CASH', fr: 'Espèces', en: 'Cash', i: '💵', c: '#c8941e' },
  { k: 'MEAL_VOUCHER', fr: 'Titre-resto', en: 'Meal voucher', i: '🍽️', c: '#9a8c70' },
];
const TABS: { k: string; fr: string; en: string }[] = [
  { k: 'Jour', fr: 'Jour', en: 'Day' },
  { k: 'Semaine', fr: 'Semaine', en: 'Week' },
  { k: 'Historique', fr: 'Historique', en: 'History' },
  { k: 'Comptabilité', fr: 'Comptabilité', en: 'Accounting' },
  { k: 'Live', fr: 'Live', en: 'Live' },
  { k: 'Partenaires', fr: 'Partenaires', en: 'Partners' },
];
const STATUS: Record<string, [string, string]> = {
  PENDING_PAYMENT: ['À encaisser', 'To charge'], RECEIVED: ['Reçue', 'Received'], PREPARING: ['En prépa', 'Preparing'],
  READY: ['Prête', 'Ready'], COMPLETED: ['Terminée', 'Done'], CANCELLED: ['Annulée', 'Cancelled'],
};

export function ManagerDashboard({ onGeneral, onPrinters, onDelivery, onStaff, initialTab }: { onGeneral: () => void; onPrinters?: () => void; onDelivery?: () => void; onStaff?: () => void; initialTab?: string }) {
  const auth = useDevice((s) => s.auth);
  const resetRole = useDevice((s) => s.resetRole);
  const setAuth = useDevice((s) => s.setAuth);
  const push = useToasts((s) => s.push);
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const t = (fr: string, en: string) => (lang === 'fr' ? fr : en);
  const loc = lang === 'fr' ? 'fr-FR' : 'en-GB';

  const fmtD = (d: Date) => d.toISOString().slice(0, 10);
  const todayStr = fmtD(new Date());
  const monthStartStr = (() => { const d = new Date(); d.setDate(1); return fmtD(d); })();
  const weekAgoStr = (() => { const d = new Date(); d.setDate(d.getDate() - 6); return fmtD(d); })();

  const [tab, setTab] = useState(initialTab ?? 'Jour');
  const [data, setData] = useState<ReportSummary | null>(null);
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [compta, setCompta] = useState<ReportSummary | null>(null);
  const [comptaFrom, setComptaFrom] = useState(monthStartStr);
  const [comptaTo, setComptaTo] = useState(todayStr);
  const [hist, setHist] = useState<ReportSummary | null>(null);
  const [histFrom, setHistFrom] = useState(weekAgoStr);
  const [histTo, setHistTo] = useState(todayStr);
  const { orders: liveOrders } = useLiveOrders('backoffice');

  const [reopenOpen, setReopenOpen] = useState(false); // fenêtre de réouverture de commande fermée
  // Alertes temps réel (ex. paiement annulé/modifié en caisse) → bannière + toast.
  const [alerts, setAlerts] = useState<{ id: number; level: string; message: string; at: string }[]>([]);
  useEffect(() => {
    const onNotif = (p: { level: 'info' | 'warn' | 'critical'; message: string }) => {
      push(`🔔 ${p.message}`, p.level === 'info' ? undefined : 'warn');
      setAlerts((a) => [{ id: Date.now() + Math.random(), level: p.level, message: p.message, at: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) }, ...a].slice(0, 6));
    };
    socket.on('notification', onNotif);
    return () => { socket.off('notification', onNotif); };
  }, [push]);

  const justif = async (id: string) => {
    push(t('🖨️ Impression du justificatif…', '🖨️ Printing receipt…'));
    try { const r = await api.printReceipt(id, 'Caisse'); push(`✅ ${t('Justificatif imprimé', 'Receipt printed')} (${r.printer})`); }
    catch (e: any) { push(`❌ ${e.message}`, 'warn'); }
  };
  const emailReceipt = async (id: string) => {
    const email = prompt(t('Email du client :', 'Customer email:'));
    if (!email || !email.trim()) return;
    try { await api.emailReceipt(id, email.trim()); push(`📧 ${t('Justificatif envoyé à', 'Receipt sent to')} ${email.trim()}`); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  const period = tab === 'Semaine' ? 'week' : 'day';
  const loadCompta = (f = comptaFrom, tt = comptaTo) => api.reportSummary({ from: f, to: tt }).then(setCompta).catch((e) => push(e.message, 'warn'));
  const loadHist = (f = histFrom, tt = histTo) => { setHistFrom(f); setHistTo(tt); api.reportSummary({ from: f, to: tt }).then(setHist).catch((e) => push(e.message, 'warn')); };
  const quick = (fromAgo: number, toAgo: number) => {
    const f = new Date(); f.setDate(f.getDate() - fromAgo);
    const tt = new Date(); tt.setDate(tt.getDate() - toAgo);
    loadHist(fmtD(f), fmtD(tt));
  };
  useEffect(() => {
    if (['Jour', 'Semaine', 'Partenaires'].includes(tab)) api.reportSummary({ period }).then(setData).catch((e) => push(e.message, 'warn'));
    if (tab === 'Partenaires') api.listPartners().then(setPartners).catch(() => {});
    if (tab === 'Comptabilité') loadCompta();
    if (tab === 'Historique') loadHist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, period]);

  const dateLabel = new Date().toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' });
  const methodTotal = data ? Object.values(data.byMethod).reduce((s, v) => s + v, 0) : 0;
  const pct = (v: number) => (methodTotal ? Math.round((v / methodTotal) * 100) : 0);
  const maxDaily = data ? Math.max(1, ...data.daily.map((d) => d.revenueCents)) : 1;
  const maxQty = data ? Math.max(1, ...data.topProducts.map((p) => p.qty)) : 1;
  const bestDate = data?.bestDay ? new Date(data.bestDay.date) : null;
  const mLabel = (k: string) => { const m = METHODS.find((x) => x.k === k); return m ? `${m.i} ${t(m.fr, m.en)}` : k; };

  const exportCsv = (d: ReportSummary) => {
    const rows: string[][] = [
      ['TAJ FUSION', `${new Date(d.from).toLocaleDateString(loc)} - ${new Date(d.to).toLocaleDateString(loc)}`],
      [t('Commandes', 'Orders'), String(d.orderCount)],
      ['Total HT', (d.htCents / 100).toFixed(2)],
      ['TVA 10%', (d.taxCents / 100).toFixed(2)],
      ['Total TTC', (d.revenueCents / 100).toFixed(2)],
      [], [t('Règlements', 'Payments'), '€'],
      ...METHODS.filter((m) => d.byMethod[m.k]).map((m) => [t(m.fr, m.en), (d.byMethod[m.k] / 100).toFixed(2)]),
      [], [t('Produit', 'Product'), t('Qté', 'Qty'), '€'],
      ...d.topProducts.map((p) => [p.name, String(p.qty), (p.revenueCents / 100).toFixed(2)]),
    ];
    const csv = '﻿' + rows.map((r) => r.join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `compta-taj-fusion-${d.from.slice(0, 10)}_${d.to.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    push(t('📥 Export CSV téléchargé', '📥 CSV export downloaded'));
  };

  const PaymentBars = () => (
    <div className="mg-card">
      <div className="mg-sec">{t('Répartition paiement', 'Payment breakdown')}</div>
      {METHODS.filter((m) => (data?.byMethod[m.k] ?? 0) > 0).map((m) => {
        const v = data!.byMethod[m.k];
        return (
          <div key={m.k} className="mg-bar-row">
            <div className="mg-bar-top"><span>{m.i} {t(m.fr, m.en)}</span><span className="mg-bar-amt">{euro(v)} · {pct(v)}%</span></div>
            <div className="mg-bar"><div className="mg-bar-fill" style={{ width: `${pct(v)}%`, background: m.c }} /></div>
          </div>
        );
      })}
      {methodTotal === 0 && <div className="no-data" style={{ padding: 10 }}>{t('Aucun paiement encaissé.', 'No payment recorded.')}</div>}
    </div>
  );

  const TopProducts = () => (
    <>
      <div className="mg-card">
        <div className="mg-sec">✅ {t('Produits vendus', 'Products sold')} · {data?.topProducts.length ?? 0} {t('réf.', 'items')}</div>
        {(data?.topProducts ?? []).map((p, i) => (
          <div key={i} className="mg-prod">
            <span className="mg-rank">{i + 1}</span>
            <span className="mg-prod-ic">{p.emoji ?? '🍽️'}</span>
            <div className="mg-prod-main">
              <div className="mg-prod-name">{p.name}</div>
              <div className="mg-bar"><div className="mg-bar-fill" style={{ width: `${(p.qty / maxQty) * 100}%`, background: 'linear-gradient(90deg,var(--gold2),var(--gold))' }} /></div>
            </div>
            <div className="mg-prod-qty">×{p.qty}<div className="mg-prod-rev">{euro(p.revenueCents)}</div></div>
          </div>
        ))}
        {(data?.topProducts.length ?? 0) === 0 && <div className="no-data" style={{ padding: 10 }}>{t('Aucune vente.', 'No sales.')}</div>}
      </div>
      <div className="mg-card">
        <div className="mg-sec" style={{ color: 'var(--orange)' }}>⚠️ {t('Produits non vendus', 'Unsold products')} · {data?.unsoldProducts.length ?? 0} {t('réf.', 'items')}</div>
        {(data?.unsoldProducts.length ?? 0) === 0 ? (
          <div className="no-data" style={{ padding: 10 }}>{t('Tous les produits se sont vendus 🎉', 'All products sold 🎉')}</div>
        ) : (
          <div className="mg-unsold">{data!.unsoldProducts.map((p, i) => <span key={i} className="mg-unsold-chip">{p.emoji ?? '🍽️'} {p.name}</span>)}</div>
        )}
      </div>
    </>
  );

  return (
    <div className="mg">
      <div className="mg-top">
        <button className="mg-back" onClick={resetRole}>←</button>
        <span className="mg-ic">📊</span>
        <div><div className="cs-staff">STAFF</div><div className="cs-role">Manager</div></div>
        <div style={{ flex: 1 }} />
        <button className="mg-pill" title={t('Comptes & droits', 'Accounts & rights')} onClick={() => onStaff?.()} style={{ cursor: 'pointer' }}>🛡️</button>
        <button
          className="mg-pill"
          title={t('Verrouiller (déconnexion) — reconnexion admin requise', 'Lock (logout) — admin login required')}
          style={{ cursor: 'pointer' }}
          onClick={() => { if (confirm(t('Verrouiller la caisse ? Il faudra se reconnecter avec un compte administrateur.', 'Lock the register? An admin account will be required to log back in.'))) setAuth(null); }}
        >🔒</button>
        <div className="cs-lang">
          <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </div>

      <div className="mg-status">
        <span><span className="ldot" /> {t('En ligne', 'Online')} · 📶 Wi-Fi · 🔄 {t('Sync temps réel', 'Real-time sync')}</span>
        <span className="mg-remote">📡 {t('Accès à distance', 'Remote access')}</span>
      </div>

      {alerts.length > 0 && (
        <div style={{ margin: '4px 0 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {alerts.map((a) => (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12,
              background: a.level === 'info' ? '#eef6ff' : '#fff3e6', border: `1px solid ${a.level === 'info' ? '#bcd8ff' : '#ffcf99'}`,
            }}>
              <span style={{ fontSize: '1.1rem' }}>🔔</span>
              <span style={{ flex: 1, fontWeight: 600 }}>{a.message}</span>
              <span style={{ color: '#999', fontSize: '.8rem' }}>{a.at}</span>
              <button className="mg-pill" onClick={() => setAlerts((x) => x.filter((y) => y.id !== a.id))}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div className="mg-tabs">
        {TABS.map((tb) => (
          <button key={tb.k} className={`mg-tab${tab === tb.k ? ' on' : ''}`} onClick={() => setTab(tb.k)}>{t(tb.fr, tb.en)}</button>
        ))}
      </div>

      <div className="mg-acts">
        <button className="mg-act" onClick={onGeneral}>⚙️ {t('Général', 'General')}</button>
        <button className="mg-act" onClick={onPrinters ?? onGeneral}>🖨️ {t('Imprimantes', 'Printers')}</button>
        <button className="mg-act" onClick={onDelivery ?? onGeneral}>🛵 {t('Livraison', 'Delivery')}</button>
        <button className="mg-act" onClick={() => setReopenOpen(true)}>🔓 {t('Rouvrir', 'Reopen')}</button>
      </div>

      {/* ── JOUR ── */}
      {tab === 'Jour' && (
        <div className="mg-body">
          <div className="mg-date">📅 {dateLabel}</div>
          <div className="mg-ca">
            <div className="mg-ca-lbl">{t('Chiffre d\'affaires', 'Revenue')}</div>
            <div className="mg-ca-val">{euro(data?.revenueCents ?? 0)}</div>
            <div className="mg-ca-sub">{data?.orderCount ?? 0} {t('commande(s)', 'order(s)')}</div>
          </div>
          <div className="mg-stats">
            <div className="mg-stat"><div className="l">{t('Commandes', 'Orders')}</div><div className="v">{data?.orderCount ?? 0}</div></div>
            <div className="mg-stat"><div className="l">{t('Panier moyen', 'Avg. basket')}</div><div className="v">{euro(data?.avgBasketCents ?? 0)}</div></div>
            <div className="mg-stat"><div className="l">{t('Plats vendus', 'Items sold')}</div><div className="v">{data?.itemsSold ?? 0}</div></div>
          </div>
          <PaymentBars />
          <div className="mg-card">
            <div className="mg-sec">{t('Sur place / emporter', 'Dine in / Takeaway')}</div>
            <div className="mg-split">
              <div className="mg-split-item"><span>🍽️ {t('Sur place', 'Dine in')}</span><b>{euro(data?.byType['DINE_IN'] ?? 0)}</b></div>
              <div className="mg-split-item"><span>🛍️ {t('À emporter', 'Takeaway')}</span><b>{euro(data?.byType['TAKEAWAY'] ?? 0)}</b></div>
            </div>
          </div>
          <TopProducts />
        </div>
      )}

      {/* ── SEMAINE ── */}
      {tab === 'Semaine' && (
        <div className="mg-body">
          <div className="mg-date">📅 {t('7 derniers jours', 'Last 7 days')}</div>
          <div className="mg-stats" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
            <div className="mg-ca">
              <div className="mg-ca-lbl">{t('CA de la semaine', 'Week revenue')}</div>
              <div className="mg-ca-val">{euro(data?.revenueCents ?? 0)}</div>
              <div className="mg-ca-sub">{data?.itemsSold ?? 0} {t('plats vendus', 'items sold')}</div>
            </div>
            <div className="mg-stat" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div className="l">{t('Meilleur jour', 'Best day')}</div>
              <div className="v">{bestDate ? bestDate.toLocaleDateString(loc, { weekday: 'short' }) : '—'}</div>
              <div style={{ color: 'var(--gold)', fontWeight: 800 }}>{euro(data?.bestDay?.revenueCents ?? 0)}</div>
            </div>
          </div>
          <div className="mg-card">
            <div className="mg-sec">{t('Rapport hebdomadaire', 'Weekly report')}</div>
            <div className="mg-chart">
              {(data?.daily ?? []).map((d, i) => {
                const dt = new Date(d.date);
                const isMax = data?.bestDay?.date === d.date;
                return (
                  <div key={i} className="mg-col">
                    <div className="mg-col-val">{Math.round(d.revenueCents / 100)}</div>
                    <div className="mg-col-bar" style={{ height: `${Math.max(6, (d.revenueCents / maxDaily) * 120)}px`, background: isMax ? 'linear-gradient(180deg,var(--gold2),var(--gold))' : 'var(--gold-line)' }} />
                    <div className="mg-col-lbl">{dt.toLocaleDateString(loc, { weekday: 'short' })}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <TopProducts />
        </div>
      )}

      {/* ── HISTORIQUE ── */}
      {tab === 'Historique' && (
        <div className="mg-body">
          <div className="mg-card">
            <div className="mg-sec">🔍 {t('Rechercher dans l\'historique', 'Search history')}</div>
            <div style={{ fontSize: '.78rem', color: 'var(--ink-dim)', marginBottom: 12 }}>{t('Choisissez une période à consulter', 'Choose a period to view')}</div>
            <div className="mg-range">
              <div><label>{t('Du', 'From')}</label><input type="date" className="inp" value={histFrom} onChange={(e) => setHistFrom(e.target.value)} /></div>
              <span>→</span>
              <div><label>{t('Au', 'To')}</label><input type="date" className="inp" value={histTo} onChange={(e) => setHistTo(e.target.value)} /></div>
            </div>
            <button className="cs-take" style={{ marginTop: 12 }} onClick={() => loadHist()}>🔍 {t('Rechercher', 'Search')}</button>
          </div>
          <div className="mg-card">
            <div className="mg-sec">{t('Périodes rapides', 'Quick periods')}</div>
            <div className="mg-quick">
              <button onClick={() => quick(0, 0)}>{t('Aujourd\'hui', 'Today')}</button>
              <button onClick={() => quick(1, 1)}>{t('Hier', 'Yesterday')}</button>
              <button onClick={() => quick(6, 0)}>{t('7 jours', '7 days')}</button>
              <button onClick={() => quick(29, 0)}>{t('30 jours', '30 days')}</button>
            </div>
          </div>
          {hist && (
            <>
              <div className="mg-stats" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                <div className="mg-ca"><div className="mg-ca-lbl">{t('CA de la période', 'Period revenue')}</div><div className="mg-ca-val">{euro(hist.revenueCents)}</div></div>
                <div className="mg-stat" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}><div className="l">{t('Commandes', 'Orders')}</div><div className="v">{hist.orderCount}</div></div>
              </div>
              <div className="mg-card">
                <div className="mg-sec">🏆 {t('Meilleures ventes', 'Best sellers')}</div>
                {hist.topProducts.map((p, i) => (
                  <div key={i} className="mg-prod">
                    <span className="mg-prod-ic">{p.emoji ?? '🍽️'}</span>
                    <div className="mg-prod-main"><div className="mg-prod-name">{p.name}</div></div>
                    <div className="mg-prod-qty">×{p.qty}<div className="mg-prod-rev">{euro(p.revenueCents)}</div></div>
                  </div>
                ))}
                {hist.topProducts.length === 0 && <div className="no-data" style={{ padding: 10 }}>{t('Aucune vente sur la période.', 'No sales for this period.')}</div>}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── COMPTABILITÉ ── */}
      {tab === 'Comptabilité' && (
        <div className="mg-body">
          <div className="mg-card">
            <div className="mg-sec">📄 {t('Comptabilité', 'Accounting')}</div>
            <div style={{ fontSize: '.78rem', color: 'var(--ink-dim)', marginBottom: 12 }}>{t('HT, TVA et TTC sur une période — à envoyer au comptable', 'Net, VAT and gross over a period — to send to the accountant')}</div>
            <div className="mg-range">
              <div><label>{t('Du', 'From')}</label><input type="date" className="inp" value={comptaFrom} onChange={(e) => setComptaFrom(e.target.value)} /></div>
              <span>→</span>
              <div><label>{t('Au', 'To')}</label><input type="date" className="inp" value={comptaTo} onChange={(e) => setComptaTo(e.target.value)} /></div>
            </div>
            <button className="cs-take" style={{ marginTop: 12 }} onClick={() => loadCompta()}>🔍 {t('Rechercher', 'Search')}</button>
          </div>
          {compta && (
            <>
              <div className="mg-card">
                <div className="mg-sec">{new Date(compta.from).toLocaleDateString(loc)} → {new Date(compta.to).toLocaleDateString(loc)}</div>
                <div style={{ fontSize: '.8rem', color: 'var(--ink-faint)', marginBottom: 6 }}>{compta.orderCount} {t('commandes', 'orders')}</div>
                <div className="mg-acc">
                  <div><span>Total HT</span><b>{euro(compta.htCents)}</b></div>
                  <div><span>TVA 10 %</span><b>{euro(compta.taxCents)}</b></div>
                  <div className="tot"><span>Total TTC</span><b>{euro(compta.revenueCents)}</b></div>
                </div>
                <button className="btn-gold" style={{ width: '100%', marginTop: 14, fontSize: '1.05rem', padding: '12px' }} onClick={() => exportCsv(compta)}>📥 {t('Exporter en CSV / Excel', 'Export to CSV / Excel')}</button>
              </div>
              <div className="mg-card">
                <div className="mg-sec">{t('Règlements', 'Payments')}</div>
                <div className="mg-acc">
                  {METHODS.filter((m) => compta.byMethod[m.k]).map((m) => (
                    <div key={m.k}><span>{m.i} {t(m.fr, m.en)}</span><b>{euro(compta.byMethod[m.k])}</b></div>
                  ))}
                </div>
                {Object.keys(compta.byMethod).length === 0 && <div className="no-data" style={{ padding: 10 }}>{t('Aucun règlement sur la période.', 'No payment for this period.')}</div>}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── LIVE ── */}
      {tab === 'Live' && (
        <div className="mg-body">
          <div className="mg-card">
            <div className="mg-sec">🔴 {t('Commandes en direct', 'Live orders')} ({liveOrders.length})</div>
            {liveOrders.map((o) => (
              <div key={o.id} className="mg-hist">
                <span className="mg-hist-num">{o.token ?? '#' + o.number}</span>
                <div className="mg-hist-main">
                  <div style={{ fontWeight: 800 }}>{o.clientName}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--ink-faint)' }}>{o.lines.map((l) => l.nameSnap).join(', ')}</div>
                </div>
                <span className={`stat-pill ${o.status}`}>{t(STATUS[o.status]?.[0] ?? o.status, STATUS[o.status]?.[1] ?? o.status)}</span>
                <span className="mg-hist-amt">{euro(o.totalCents)}</span>
                <button className="cs-btn" style={{ marginLeft: 8, padding: '6px 10px' }} title={t('Imprimer le justificatif', 'Print receipt')} onClick={() => justif(o.id)}>📄</button>
                <button className="cs-btn" style={{ marginLeft: 6, padding: '6px 10px' }} title={t('Envoyer par email', 'Send by email')} onClick={() => emailReceipt(o.id)}>📧</button>
              </div>
            ))}
            {liveOrders.length === 0 && <div className="no-data" style={{ padding: 14 }}>{t('Aucune commande active.', 'No active order.')}</div>}
          </div>
        </div>
      )}

      {/* ── PARTENAIRES ── */}
      {tab === 'Partenaires' && (
        <div className="mg-body">
          <div className="mg-card">
            <div className="mg-sec">{t('Partenaires de livraison', 'Delivery partners')}</div>
            {partners.length === 0 && <div className="no-data" style={{ padding: 10, fontSize: '.82rem' }}>{t('Aucun partenaire. Ajoute-les dans Général → Partenaires.', 'No partner yet. Add them in General → Partners.')}</div>}
            {partners.filter((p) => p.active).map((p) => {
              const pr = data?.partners?.find((x) => x.partnerId === p.id);
              const gross = pr?.grossCents ?? 0;
              const commission = pr?.commissionCents ?? Math.round((gross * p.commissionPct) / 100);
              const net = pr?.netCents ?? (gross - commission);
              return (
                <div key={p.id} className="mg-part">
                  <div style={{ fontWeight: 900 }}>{p.type} <span style={{ color: 'var(--ink-faint)', fontWeight: 700 }}>· {t('commission', 'commission')} {p.commissionPct}% {pr?.count ? `· ${pr.count} cmd` : ''}</span></div>
                  <div className="mg-part-row"><span>{t('CA brut', 'Gross')}</span><b>{euro(gross)}</b></div>
                  <div className="mg-part-row"><span>{t('Commission', 'Commission')}</span><b style={{ color: 'var(--cuisine)' }}>-{euro(commission)}</b></div>
                  <div className="mg-part-row tot"><span>{t('Net', 'Net')}</span><b style={{ color: 'var(--green)' }}>{euro(net)}</b></div>
                </div>
              );
            })}
            <div className="no-data" style={{ padding: 10, fontSize: '.78rem' }}>💡 {t('Prends une commande partenaire en caisse (sélecteur Uber Eats / Deliveroo) : elle apparaît ici avec sa commission.', 'Take a partner order at the till (Uber Eats / Deliveroo selector): it shows here with its commission.')}</div>
          </div>
        </div>
      )}

      <div className="cs-device" onClick={onGeneral}>⚙ Manager · {auth?.staff.fullName}</div>
      {reopenOpen && <ReopenModal onClose={() => setReopenOpen(false)} />}
    </div>
  );
}
